import type { Issue } from "../lib/tasks";

/** ══ 목업 데이터 ══
 *  보드(`BoardPage`)와 카드(`IssueCard`)는 이제 `GET /tasks` 를 읽는다(t-in-web-api-wire) —
 *  이 파일이 남은 이유는 **패턴 페이지(`#/patterns`)의 보기용 자료**뿐이다. 실제 화면이
 *  이 파일을 다시 import 하면 그 순간 원상복구다.
 *
 *  「잘 나오는 데이터」만 넣으면 실제 데이터가 들어올 때 깨지고, 그때는 이미 API 까지
 *  얽혀 있어 원인을 찾기 어렵다. 그래서 일부러 험한 것을 섞었다 —
 *  빈 열 · 한 건짜리 열 · 카드가 많은 열 · 아주 긴 제목 · 값이 빠진 행 ·
 *  이상한 조합(막힘인데 세션이 열려 있음, 검토인데 PR 없음, 취소된 완료 열 카드). */

export type { Issue };

export const MILESTONES: Record<string, string> = {
  "in-m2-design": "M3 화면 시안", // copy-ignore: 견본 자료
  "in-m3-screens": "M4 화면 세우기", // copy-ignore: 견본 자료
  "in-m4-core": "M5 코어와 MCP", // copy-ignore: 견본 자료
};

/** 목업 시각의 기준점. **패턴 페이지가 `minutesSince` 에 그대로 넘긴다** — 실제 지금을
 *  쓰면 이 파일의 고정 시각들이 「몇 분째」가 아니라 「몇 주째」로 보인다. */
export const MOCK_NOW = Date.parse("2026-09-12T04:00:00Z");
const ago = (min: number) => new Date(MOCK_NOW - min * 60_000).toISOString();

let rank = 1000;
function issue(p: Partial<Issue> & Pick<Issue, "id" | "title" | "status">): Issue {
  return {
    type: "feature",
    priority: "normal",
    complexity: "normal",
    milestoneId: "in-m3-screens",
    parentId: null,
    childCount: 0,
    doneChildCount: 0,
    assignee: null,
    assigneeKind: null,
    prLink: null,
    queueRank: (rank += 1000),
    updatedAt: ago(30),
    openSession: null,
    agentRequestedAt: null,
    intent: "",
    acceptance: [],
    dependsOn: [],
    ...p,
  };
}

export const ISSUES: Issue[] = [
  // ── 에픽 하나와 그 하위들. 에픽은 박스로 안 묶고 카드 표식(◇ + 분수)만 쓴다 ──
  issue({
    id: "t-in-epic-m3",
    title: "M4 화면 세우기 (에픽)", // copy-ignore: 견본 자료
    status: "in-progress",
    type: null, // 미분류 — 화면이 이 빈칸을 그대로 그리는지 보는 자리다
    priority: "high",
    childCount: 5,
    doneChildCount: 2,
    intent: "M4 의 화면 넷을 세운다. 하위가 전부 닫히면 이 에픽도 닫힌다.", // copy-ignore: 견본 자료
  }),
  issue({
    id: "t-in-screen-pattern",
    title: "디자인 패턴 페이지 — 타입 스케일과 컴포넌트를 실물로 세운다", // copy-ignore: 견본 자료
    status: "review",
    parentId: "t-in-epic-m3",
    priority: "high",
    complexity: "hard",
    assignee: "admin",
    assigneeKind: "held",
    prLink: { url: "https://github.com/irolab-co/b3x-in/pull/11", createdAt: ago(12) },
    updatedAt: ago(3),
    intent: "시안이 정한 타입 스케일을 토큰으로 올리고 패턴 페이지로 실물을 세운다.", // copy-ignore: 견본 자료
    acceptance: ["네비바에서 바로 갈 수 있다", "화면 CSS 에 px 글자 크기가 없다"], // copy-ignore: 견본 자료
  }),
  issue({
    id: "t-in-screen-board",
    title: "이슈 보드 · 목록 · 상세 화면", // copy-ignore: 견본 자료
    status: "in-progress",
    parentId: "t-in-epic-m3",
    assignee: "admin",
    assigneeKind: "held",
    openSession: { id: "ses-mock-8", runner: "naru-8", startedAt: ago(12) },
    updatedAt: ago(1),
    dependsOn: ["t-in-design-board", "t-in-screen-pattern"],
    intent: "채택된 시안대로 이슈 화면 셋을 그린다. 목업 데이터로 채우고 API 는 만들지 않는다.", // copy-ignore: 견본 자료
    acceptance: ["보드·목록·상세가 시안대로 그려진다", "새 API 를 만들지 않았다"], // copy-ignore: 견본 자료
  }),
  issue({
    id: "t-in-screen-conn",
    title: "연결 설정 화면 — 실제로 ola-mops 에 붙는다", // copy-ignore: 견본 자료
    status: "ready",
    parentId: "t-in-epic-m3",
    complexity: "hard",
    dependsOn: ["t-in-screen-pattern"],
    intent: "주소·토큰·모델을 넣고 연결 시험을 눌러 결과를 본다.", // copy-ignore: 견본 자료
  }),
  issue({
    id: "t-in-screen-session",
    title: "세션 진행 화면", // copy-ignore: 견본 자료
    status: "todo",
    parentId: "t-in-epic-m3",
    type: "design",
    intent: "세션이 지금 무엇을 하고 있는지 본다.", // copy-ignore: 견본 자료
  }),

  // ── 검토 열: 병합 대기(PR 있음)와 아직 검토 중(PR 없음)이 섞여 있어야 구분이 보인다 ──
  issue({
    id: "t-in-api-health",
    title: "연결 시험 API 리뷰", // copy-ignore: 견본 자료
    status: "review",
    milestoneId: "in-m4-core",
    complexity: "hard",
    updatedAt: ago(75),
    intent: "PR 이 아직 없다 — 검토 중이지 병합 대기가 아니다.", // copy-ignore: 견본 자료
  }),
  issue({
    id: "t-in-tokens",
    title: "타입 스케일 토큰화", // copy-ignore: 견본 자료
    status: "review",
    milestoneId: "in-m2-design",
    priority: "high",
    prLink: { url: "https://github.com/irolab-co/b3x-in/pull/9", createdAt: ago(200) },
    updatedAt: ago(190),
    intent: "PR 이 오래 열려 있다 — 병합 대기가 길어지는 것도 보여야 한다.", // copy-ignore: 견본 자료
  }),

  // ── 이상한 조합들. 있을 수 없어 보이지만 실제로 생긴다 ──
  issue({
    id: "t-in-odd-blocked",
    title: "막힘인데 세션이 열려 있다 — 세션이 죽기 전에 막힘으로 옮겨진 경우다", // copy-ignore: 견본 자료
    status: "blocked",
    priority: "critical",
    assignee: "naru-3",
    assigneeKind: "held",
    openSession: { id: "ses-mock-3", runner: "naru-3", startedAt: ago(240) },
    updatedAt: ago(240),
    intent: "「도는 중」을 상태에서 파생시키면 이 카드가 안 돈다. 열린 세션이 신호다.", // copy-ignore: 견본 자료
  }),
  issue({
    id: "t-in-odd-long",
    title:
      "아주 긴 제목이 카드와 표를 어떻게 밀어내는지 보는 이슈 — 폐쇄망 설치에서 이슈 제목을 문장으로 적는 사람이 실제로 있고, 그때 카드가 세로로 늘어나는지 잘리는지 여기서 정한다", // copy-ignore: 견본 자료
    status: "todo",
    milestoneId: null, // 마일스톤 없음 — 표의 그 칸이 비었을 때를 본다
    complexity: "deep",
    priority: "high",
    assigneeKind: "ticket",
    assignee: "admin",
    intent: "긴 제목 · 마일스톤 없음 · 사람이 찍어 둔 담당(ticket) 셋이 한 카드에 모여 있다.", // copy-ignore: 견본 자료
  }),
  issue({
    id: "t-in-odd-cancelled",
    title: "취소된 이슈", // copy-ignore: 견본 자료
    status: "cancelled",
    type: "chore",
    updatedAt: ago(1440),
    intent: "완료 열에 취소가 섞여 든다. 취소선과 ⊘ 로 완료와 구분한다 — 색만으로 가르지 않는다.", // copy-ignore: 견본 자료
  }),
];

/* 「대기」 열을 많은 카드로 채운다 — 열 하나가 길어졌을 때 스크롤과 끌어 옮기기가
   어떻게 되는지는 카드가 몇 장 있을 때는 절대 안 드러난다. */
for (let i = 1; i <= 14; i += 1) {
  ISSUES.push(
    issue({
      id: `t-in-bulk-${i}`,
      title: `대기 중인 잡일 ${i}`, // copy-ignore: 견본 자료
      status: "ready",
      type: "chore",
      milestoneId: "in-m4-core",
      complexity: i % 3 === 0 ? "light" : "normal",
      updatedAt: ago(60 + i * 7),
      intent: "열이 길어졌을 때를 보려고 채운 줄이다.", // copy-ignore: 견본 자료
    }),
  );
}

/* 「할 일」 열에 한 건만 남기고, 「진행」 열에도 한 건뿐이다. 빈 열은 「완료」가 아니라
   실제로 비는 자리를 만들어야 하므로 필터를 걸면 드러난다(빈 열 안내 문구를 그 자리에 그린다). */
